import React, { useEffect, useState } from "react";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiSend,
  FiChevronUp,
  FiExternalLink,
  FiCheckCircle,
  FiX,
} from "react-icons/fi";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";

import { Check } from "lucide-react";

import toast from "react-hot-toast";
import { useUserContext } from "../../context/UserContext";
import useExamStores from "../../stores/useExamStores";
import useExamServices from "../../context/useExamServices";
// import { useExamContext } from "../../context/ExamContext";

declare global {
  interface Window {
    fbq?: (event: string, ...args: any[]) => void;
  }
}
export type TypeContactFormData = {
  name: string;
  email: string;
  candidates: string;
  examtype: string;
  aienabled: boolean;
  examcount: string;
  message: string;
  phone: string;
};

const ContactUS = () => {
  const initialFormData: TypeContactFormData = {
    name: "",
    email: "",
    candidates: "",
    examtype: "Online",
    aienabled: false,
    examcount: "",
    message: "",
    phone: "",
  };
  const examContext = useExamStores();
  const examService = useExamServices();
  const [showSubmissionModal, setShowSubmissionModal] =
    useState<boolean>(false);

  const [formData, setFormData] =
    useState<TypeContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;

    let newValue = "";
    if (name === "phone") {
      newValue = value.replace(/[^0-9]/g, "")?.slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();

    (async () => {
      await examService.ApplyContactForm({
        payload: formData,
        successCallback: () => {
          setShowSubmissionModal(true);
        },
        errorCallback: () => {
          toast.error("something went wrong");
        },
      });
    })();
  };
  useEffect(() => {
    if (!showSubmissionModal) {
      setFormData(initialFormData);
    } else {
      if (typeof window.fbq === "function") {
        window.fbq("track", "Purchase");
      }
    }
  }, [showSubmissionModal]);

  return (
    <section className="py-20 px-4 ">
      {showSubmissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 border border-gray-100">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-2">
                <Check className="w-10 h-10 text-white" />
              </div>

              <h3 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                Message Sent!
              </h3>

              <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                Thank you for reaching out! We've received your message and will
                respond within 3 business day
              </p>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-3 mb-3 text-left">
                <h4 className="font-bold text-gray-900  text-center">
                  Submission Summary
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Name:</span>
                    <span className="text-gray-900">{formData.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Email:</span>
                    <span className="text-gray-900">{formData.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">
                      Candidates:
                    </span>
                    <span className="text-gray-900">{formData.candidates}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Exams:</span>
                    <span className="text-gray-900">{formData.examcount}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="font-semibold text-gray-700">Type:</span>
                    <span className="text-gray-900 capitalize">
                      {formData.examtype}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-semibold text-gray-700">
                      AI Proctoring:
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        formData.aienabled
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {formData.aienabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowSubmissionModal(false);
                    setFormData(initialFormData);
                  }}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        {/* Compact Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 animate-gradient mb-4">
            Get in Touch
          </h2>
          <div className="flex justify-center mb-2">
            <span className="inline-block w-28 h-[5px] bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-md "></span>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions or want to book a free demo? Reach out directly or
            fill the form.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Methods - Compact Cards */}
          <div className="space-y-4 ">
            <motion.div
              whileHover={{ y: -3 }}
              className="flex items-start p-4 bg-white rounded-lg border border-gray-200 shadow-inner cursor-pointer"
            >
              <div className="p-2 bg-blue-100 text-blue-600 rounded-full mr-4">
                <FiMail className="text-lg" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Email</h3>
                <p className="text-sm text-gray-600">cbt@msqube.com</p>
                <a
                  href="mailto:cbt@msqube.com"
                  className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                >
                  Send message
                </a>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ y: -3 }}
              className="flex items-start p-4 bg-white rounded-lg border border-gray-200 shadow-inner cursor-pointer"
            >
              <div className="p-2 bg-green-100 text-green-600 rounded-full mr-4">
                <FiPhone className="text-lg" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Phone</h3>
                <p className="text-sm text-gray-600">033-4814-4250</p>
                <a
                  href="tel:03348144250"
                  className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                >
                  Call now
                </a>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ y: -3 }}
              className=" bg-white rounded-lg border border-gray-200 shadow-inner cursor-pointer overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-start">
                  <div className="p-2 bg-purple-100 text-purple-600 rounded-lg mr-4">
                    <FiMapPin className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      Our Location
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Unit #112, SDF Building Near RDB Cinemas,
                      <br />
                      GP Block, Sector V, Bidhannagar
                      <br />
                      Kolkata, West Bengal 700091
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-lg overflow-hidden border border-gray-200">
                  <iframe
                    src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=MSQube%20Technology,%20company,%20Bidhannagar,%20India+(MSQUBE)&amp;t=&amp;z=16&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    className="block"
                  />
                </div>

                <div className="mt-3 text-right">
                  <a
                    href="https://maps.app.goo.gl/UQmk69nuaEEebHmS8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    Open in Google Maps
                    <FiExternalLink className="ml-1.5" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Compact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white p-6 rounded-lg border border-gray-200 shadow-inner"
          >
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-1">
                Contact Us
              </h3>
              <p className="text-gray-500 text-sm">
                Fill out the form and we'll get back to you shortly
              </p>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="col-span-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    type="text"
                    name="phone"
                    maxLength={10}
                    value={formData.phone}
                    autoComplete="off"
                    autoCorrect="off"
                    onChange={handleChange}
                    placeholder="Phone"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email address"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="candidates"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Number of Candidates <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="candidates"
                  name="candidates"
                  value={formData.candidates}
                  onChange={handleChange}
                  placeholder="e.g. 100"
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                  required
                  min="1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div>
                    <label
                      htmlFor="examcount"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      How many exams do you conduct ?{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="examcount"
                      name="examcount"
                      value={formData.examcount}
                      onChange={handleChange}
                      placeholder="e.g. 12"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                      required
                      min="1"
                    />
                  </div>
                </motion.div>

                <div>
                  <motion.label
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center space-x-2 p-2.5 border rounded-lg cursor-pointer transition-colors mt-6 ${
                      formData.aienabled
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="aienabled"
                      checked={formData.aienabled}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-500 focus:ring-blue-400 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable AI Proctoring Features
                    </span>
                  </motion.label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exam Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {["Online", "Offline", "Hybrid"].map((type) => (
                    <motion.label
                      key={type}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.examtype === type
                          ? "border-blue-400 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="examtype"
                        value={type}
                        checked={formData.examtype === type}
                        onChange={handleChange}
                        className="h-4 w-4 text-blue-500 focus:ring-blue-400"
                      />
                      <span className="text-sm font-medium">{type}</span>
                    </motion.label>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements..."
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
                  required
                ></textarea>
              </div>

              <button
                onClick={handleSubmit}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg text-sm font-semibold transition-all flex items-center justify-center shadow-sm"
              >
                Send <FiSend className="ml-2" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactUS;
